import { BaseFeature } from '../../core/base-feature.js';
import { CHAT_MODES, MODE_TOGGLES, normalizeModeConfig } from './chat-modes.js';

/**
 * ChatFeature - Text chat functionality (Frontend)
 *
 * Handles:
 * - Session management (start, end, state)
 * - Display mode control (fullscreen, immovable, transparent, corner, subliminal, crawl, peripheral)
 * - AI mode control (human, ai, augmented, random_checkin)
 * - Message sending/receiving
 * - History retrieval and search
 * - Event subscriptions for React components
 *
 * Lifecycle:
 * - initialize(): Called during registration (no DOM)
 * - onActivate(): Called when chat view mounts (initializes DOM)
 * - onDeactivate(): Called when chat view unmounts (cleans up DOM)
 */
export class ChatFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // DOM element references (set during activation)
        this.chatMessages = null;
        this.chatInput = null;
        this.sendChatBtn = null;

        // Event listener references (for cleanup)
        this.boundSendMessage = null;
        this.boundKeyPress = null;

        // Track desktop peer ID for sending commands
        this.desktopPeerId = null;

        // Track if chat is enabled (connected to peer)
        this.chatEnabled = false;

        // Session state
        this.currentSession = null;
        this.currentModeConfig = { mode: 'conversation', style: null, toggles: {} };
        this.currentAiMode = 'human';

        // AI suggestion (augmented mode)
        this.pendingSuggestion = null;

        // Event subscriptions (for React components)
        this.eventSubscribers = new Map();

        // Message history cache
        this.messageHistory = [];

        // Pending requests for request/response pattern
        this.pendingRequests = new Map();

        // Cached personality presets (fetched from desktop)
        this.personalityPresets = null;

        // AI auto mode state
        this.aiAutoSettings = null;
        this.aiSchedule = null;
        this.aiConversationState = null;
    }

    static getMetadata() {
        return {
            id: 'chat',
            name: 'Chat',
            version: 'v2.0',
            capabilities: [
                'send_message',
                'receive_message',
                'display_history',
                'session_management',
                'display_modes',
                'ai_modes',
                'history_search'
            ],
            dependencies: []
        };
    }

    /**
     * Lightweight initialization - NO DOM access
     * Called during feature registration
     */
    async initialize() {
        console.log('[Chat] Registered (DOM initialization deferred until activation)');
    }

    /**
     * Activate feature - Initialize DOM elements
     * Called when chat view mounts
     */
    async onActivate() {
        console.log('[Chat] Activating - initializing DOM...');

        // Get DOM element references
        this.chatMessages = document.getElementById('chatMessages');
        this.chatInput = document.getElementById('chatInput');
        this.sendChatBtn = document.getElementById('sendChatBtn');

        console.log('[Chat] DOM elements found:', {
            chatMessages: !!this.chatMessages,
            chatInput: !!this.chatInput,
            sendChatBtn: !!this.sendChatBtn
        });

        if (this.chatInput && this.sendChatBtn) {
            // Create bound event handlers (for cleanup later)
            this.boundSendMessage = () => this.sendChatMessage();
            this.boundKeyPress = (e) => {
                if (e.key === 'Enter') {
                    this.sendChatMessage();
                }
            };

            // Set up event listeners
            this.sendChatBtn.addEventListener('click', this.boundSendMessage);
            this.chatInput.addEventListener('keypress', this.boundKeyPress);
        }

        // Set initial enabled/disabled state based on connection status
        if (this.chatEnabled) {
            this.enableUI();
        } else {
            this.disableUI();
        }

        console.log('[Chat] Activated');
    }

    /**
     * Deactivate feature - Clean up DOM elements
     * Called when chat view unmounts
     */
    async onDeactivate() {
        console.log('[Chat] Deactivating - cleaning up DOM...');

        // Remove event listeners
        if (this.sendChatBtn && this.boundSendMessage) {
            this.sendChatBtn.removeEventListener('click', this.boundSendMessage);
        }
        if (this.chatInput && this.boundKeyPress) {
            this.chatInput.removeEventListener('keypress', this.boundKeyPress);
        }

        // Clear DOM references
        this.chatMessages = null;
        this.chatInput = null;
        this.sendChatBtn = null;

        // Clear bound handlers
        this.boundSendMessage = null;
        this.boundKeyPress = null;

        console.log('[Chat] Deactivated');
    }

    /**
     * Enable UI elements
     */
    enableUI() {
        if (this.chatInput) this.chatInput.disabled = false;
        if (this.sendChatBtn) this.sendChatBtn.disabled = false;
    }

    /**
     * Disable UI elements
     */
    disableUI() {
        if (this.chatInput) this.chatInput.disabled = true;
        if (this.sendChatBtn) this.sendChatBtn.disabled = true;
    }

    // ==================== Message Handling ====================

    async handleMessage(message) {
        const { action, payload, messageId } = message;
        // originalMessageId may be at top level or inside payload (from _sendResponse)
        const originalMessageId = message.originalMessageId || (payload && payload.originalMessageId);

        // Check if this is a response to a pending request
        if (originalMessageId && this.pendingRequests.has(originalMessageId)) {
            this.handleResponse(originalMessageId, payload);
            return;
        }

        switch (action) {
            // Legacy: Display incoming chat
            case 'chat':
                return this.displayChatMessage(payload);

            // New: Chat events from desktop
            case 'chat_event':
                return this.handleChatEvent(payload);

            // AI suggestion received
            case 'ai_suggestion':
                return this.handleAiSuggestion(payload);

            // AI pending message (for AI Auto mode countdown)
            case 'ai_pending_message':
                return this.handleAiPendingMessage(payload);

            // AI thinking state
            case 'ai_thinking':
                return this.emitEvent('ai_thinking', payload);

            // Session events
            case 'session_started':
                return this.handleSessionStarted(payload);

            case 'session_ended':
                return this.handleSessionEnded(payload);

            // Message events
            case 'message_received':
                return this.handleMessageReceived(payload);

            case 'message_sent':
                return this.handleMessageSent(payload);

            // Typing updates
            case 'typing_update':
                return this.handleTypingUpdate(payload);

            // AI auto mode events
            case 'ai_schedule_update':
                return this.handleAiScheduleUpdate(payload);

            case 'ai_conversation_state':
                return this.handleAiConversationState(payload);

            case 'active_punishments':
                return this.emitEvent('active_punishments', payload);

            case 'ai_pending_actions':
                return this.emitEvent('ai_pending_actions', payload);

            case 'response_countdown':
                return this.emitEvent('response_countdown', payload);

            case 'chat_closing':
                return this.emitEvent('chat_closing', payload);

            // Broadcast duration events
            case 'broadcast_started':
                return this.emitEvent('broadcast_started', payload);

            case 'broadcast_tick':
                return this.emitEvent('broadcast_tick', payload);

            case 'broadcast_ended':
                return this.emitEvent('broadcast_ended', payload);

            case 'broadcast_acknowledged':
                return this.emitEvent('broadcast_acknowledged', payload);

            // Response actions (for request/response pattern)
            // Normally handled via originalMessageId check above, but fallback here
            case 'get_sessions_response':
            case 'get_messages_response':
            case 'get_current_session_messages_response':
            case 'search_messages_response':
            case 'export_session_response':
            case 'get_personality_presets_response':
            case 'get_ai_auto_settings_response':
            case 'set_ai_auto_settings_response':
            case 'get_ai_conversation_state_response':
            case 'pause_schedule_response':
            case 'resume_schedule_response':
            case 'reset_defiance_response':
            case 'adjust_schedule_response':
            case 'edit_message_response':
            case 'delete_message_response':
            case 'get_activity_triggers_response':
            case 'set_activity_triggers_response':
            case 'get_autonomy_settings_response':
            case 'set_autonomy_settings_response':
            case 'add_to_dossier_response':
            case 'get_acknowledgment_stats_response':
                if (originalMessageId && this.pendingRequests.has(originalMessageId)) {
                    this.handleResponse(originalMessageId, payload);
                }
                return;

            default:
                console.warn(`[Chat] Unknown action: ${action}`);
        }
    }

    handleChatEvent(payload) {
        // Route chat events to appropriate handlers
        switch (payload.type) {
            case 'ai_suggestion':
                this.handleAiSuggestion(payload.suggestion);
                break;
            default:
                this.emitEvent('chat_event', payload);
        }
    }

    handleSessionStarted(session) {
        this.currentSession = session;
        this.currentAiMode = session.aiMode || 'human';
        this.currentModeConfig = session.modeConfig || normalizeModeConfig(session.displayMode || 'conversation');
        this.emitEvent('session_started', session);
    }

    handleSessionEnded(session) {
        this.currentSession = null;
        this.emitEvent('session_ended', session);
    }

    handleMessageReceived(message) {
        this.messageHistory.push(message);
        this.emitEvent('message_received', message);

        // Surface AI tool actions to the cockpit's unified activity feed. Every
        // executed tool (lock_screen, speak, block_app, …) posts its own system
        // message tagged metadata.type === 'tool_action'. AICockpit subscribes to
        // 'tool_action' to build its recent-actions list, but nothing ever emitted
        // it — so chat AI actions never appeared there, and a multi-tool turn
        // (e.g. lock + speak) showed neither. Re-emit one event per tool-action
        // message so every action in the turn is reflected.
        let md = message && message.metadata;
        if (typeof md === 'string') {
            try { md = JSON.parse(md); } catch { md = null; }
        }
        if (md && md.type === 'tool_action') {
            this.emitEvent('tool_action', md);
        }
    }

    handleMessageSent(message) {
        this.messageHistory.push(message);
        this.emitEvent('message_sent', message);
    }

    handleAiSuggestion(suggestion) {
        this.pendingSuggestion = suggestion;
        this.emitEvent('ai_suggestion', suggestion);
    }

    handleAiPendingMessage(pending) {
        // Emit to React component for countdown UI
        this.emitEvent('ai_pending_message', pending);
    }

    handleTypingUpdate(update) {
        this.emitEvent('typing_update', update);
    }

    handleAiScheduleUpdate(schedule) {
        this.aiSchedule = schedule;
        this.emitEvent('ai_schedule_update', schedule);
    }

    handleAiConversationState(state) {
        this.aiConversationState = state;
        this.emitEvent('ai_conversation_state', state);
    }

    // ==================== Session Management ====================

    /**
     * Start a new chat session
     * Note: sendMessage is fire-and-forget, session_started event confirms success
     */
    async startSession(options = {}) {
        const {
            aiMode = 'human',
            displayMode = 'immovable',
            aiPersonality = null
        } = options;

        console.log('[Chat] Starting session:', options);

        // Optimistically set state - will be confirmed by session_started event
        this.currentAiMode = aiMode;
        this.currentModeConfig = normalizeModeConfig(displayMode);

        this.sendMessage('start_session', {
            aiMode,
            displayMode,
            modeConfig: normalizeModeConfig(displayMode),
            aiPersonality
        });

        // Return success optimistically - the session_started event will provide session details
        return { success: true, session: { aiMode, displayMode } };
    }

    /**
     * End the current session
     * Note: sendMessage is fire-and-forget, session_ended event confirms success
     */
    async endSession(reason = 'normal') {
        console.log('[Chat] Ending session:', reason);

        this.sendMessage('end_session', { reason });

        // Optimistically clear state
        this.currentSession = null;

        return { success: true };
    }

    /**
     * Check if session is active
     */
    hasActiveSession() {
        return this.currentSession !== null;
    }

    /**
     * Get current session info
     */
    getSessionInfo() {
        return {
            session: this.currentSession,
            aiMode: this.currentAiMode,
            displayMode: this.currentModeConfig.mode,
            modeConfig: this.currentModeConfig,
            hasSuggestion: this.pendingSuggestion !== null
        };
    }

    // ==================== Display Mode ====================

    async setModeConfig(modeConfig) {
        const normalized = normalizeModeConfig(modeConfig);
        console.log('[Chat] Setting mode config:', normalized.mode);
        const result = await this.sendMessage('set_mode_config', normalized);
        if (result?.success) {
            this.currentModeConfig = normalized;
        }
        return result;
    }

    // Legacy compat
    async setDisplayMode(mode) {
        return this.setModeConfig(mode);
    }

    getModes() {
        return Object.values(CHAT_MODES);
    }

    getModeToggles(mode) {
        return MODE_TOGGLES[mode] || {};
    }

    // ==================== AI Mode ====================

    /**
     * Set AI mode
     */
    async setAiMode(mode, personality = null) {
        console.log('[Chat] Setting AI mode:', mode);

        const result = await this.sendMessage('set_ai_mode', {
            mode,
            personality
        });

        if (result?.success) {
            this.currentAiMode = mode;
        }

        return result;
    }

    /**
     * Get available AI modes
     */
    getAiModes() {
        return [
            { id: 'human', name: 'Human', description: 'You type directly' },
            { id: 'ai', name: 'AI', description: 'AI responds autonomously (disguised as human)' },
            { id: 'augmented', name: 'Augmented', description: 'AI suggests, you approve/edit' },
            { id: 'random_checkin', name: 'Random Check-in', description: 'AI initiates at random intervals' }
        ];
    }

    /**
     * Get AI personality presets (returns cached if available)
     */
    getPersonalityPresets() {
        return this.personalityPresets || [];
    }

    /**
     * Fetch personality presets from desktop and cache them
     */
    async fetchPersonalityPresets() {
        try {
            console.log('[Chat] Fetching personality presets from desktop...');
            const result = await this.sendRequest('get_personality_presets', {});

            if (result?.success && result.presets) {
                this.personalityPresets = result.presets;
                console.log('[Chat] Synced personality presets:', this.personalityPresets.map(p => p.id));
                // Notify subscribers that presets have updated
                this.emitEvent('personality_presets_updated', this.personalityPresets);
            }

            return result;
        } catch (error) {
            console.error('[Chat] Failed to fetch personality presets:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== AI Auto Mode Settings ====================

    /**
     * Get AI auto mode settings from desktop
     */
    async getAiAutoSettings() {
        try {
            const result = await this.sendRequest('get_ai_auto_settings', {});
            if (result?.success) {
                this.aiAutoSettings = result.settings;
            }
            return result;
        } catch (error) {
            console.error('[Chat] Failed to get AI auto settings:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Set AI auto mode settings
     */
    async setAiAutoSettings(settings) {
        try {
            const result = await this.sendRequest('set_ai_auto_settings', settings);
            if (result?.success) {
                this.aiAutoSettings = result.settings;
                this.emitEvent('ai_auto_settings_updated', result.settings);
            }
            return result;
        } catch (error) {
            console.error('[Chat] Failed to set AI auto settings:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Get AI conversation state
     */
    async getAiConversationState() {
        try {
            const result = await this.sendRequest('get_ai_conversation_state', {});
            if (result?.success) {
                this.aiConversationState = result.state;
            }
            return result;
        } catch (error) {
            console.error('[Chat] Failed to get AI conversation state:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Trigger AI initiation manually
     */
    async triggerAiInitiation() {
        try {
            this.sendMessage('trigger_ai_initiation', {});
            return { success: true };
        } catch (error) {
            console.error('[Chat] Failed to trigger AI initiation:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Request a fresh advisor-mode suggestion based on the last desktop message.
     * Replaces the previous auto-generate-on-every-message behavior.
     */
    async triggerAiSuggestion() {
        try {
            this.sendMessage('trigger_ai_suggestion', {});
            return { success: true };
        } catch (error) {
            console.error('[Chat] Failed to trigger AI suggestion:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Get cached AI schedule
     */
    getAiSchedule() {
        return this.aiSchedule || null;
    }

    /**
     * Get cached conversation state
     */
    getCachedConversationState() {
        return this.aiConversationState || null;
    }

    /**
     * Pause the AI initiation schedule
     */
    async pauseSchedule() {
        try {
            const result = await this.sendRequest('pause_schedule', {});
            return result;
        } catch (error) {
            console.error('[Chat] Failed to pause schedule:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Resume the AI initiation schedule
     */
    async resumeSchedule() {
        try {
            const result = await this.sendRequest('resume_schedule', {});
            return result;
        } catch (error) {
            console.error('[Chat] Failed to resume schedule:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Reset defiance level back to 0
     */
    async resetDefiance() {
        try {
            const result = await this.sendRequest('reset_defiance', {});
            return result;
        } catch (error) {
            console.error('[Chat] Failed to reset defiance:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Adjust the AI initiation schedule
     * @param {Object} options - { newTime?: number, addMinutes?: number }
     */
    async adjustSchedule(options) {
        try {
            const result = await this.sendRequest('adjust_schedule', options);
            return result;
        } catch (error) {
            console.error('[Chat] Failed to adjust schedule:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== Autonomy Settings ====================

    /**
     * Get autonomy settings from desktop
     */
    async getAutonomySettings() {
        try {
            return await this.sendRequest('get_autonomy_settings', {});
        } catch (error) {
            console.error('[Chat] Failed to get autonomy settings:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Set autonomy settings
     */
    async setAutonomySettings(settings) {
        try {
            return await this.sendRequest('set_autonomy_settings', settings);
        } catch (error) {
            console.error('[Chat] Failed to set autonomy settings:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== Screenshot Gallery ====================

    async getScreenshotGallery() {
        try {
            return await this.sendRequest('get_screenshot_gallery', {});
        } catch (error) {
            console.error('[Chat] Failed to get screenshot gallery:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== Enforcement Configuration ====================

    async getEnforcementConfig() {
        try {
            return await this.sendRequest('get_enforcement_config', {});
        } catch (error) {
            console.error('[Chat] Failed to get enforcement config:', error);
            return { success: false, error: error.message };
        }
    }

    async setEnforcementConfig(config) {
        try {
            const result = await this.sendRequest('set_enforcement_config', config);
            if (result?.success) {
                this.emitEvent('enforcement_config_updated', result.config);
            }
            return result;
        } catch (error) {
            console.error('[Chat] Failed to set enforcement config:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== Message Sending ====================

    /**
     * Send a chat message
     * Note: sendMessage is fire-and-forget, message_sent event confirms delivery
     */
    async sendChatMessageToDesktop(messageText, options = {}) {
        const {
            displayMode = this.currentModeConfig.mode,
            modeConfig = null,
            senderDisplayName = 'Controller',
            aiGenerated = false,
            extraData = null
        } = options;

        console.log('[Chat] Sending message:', messageText?.substring(0, 50), aiGenerated ? '(AI)' : '(human)');

        // Send via DataChannel
        this.sendMessage('send_message', {
            message: messageText,
            displayMode,
            modeConfig: modeConfig || this.currentModeConfig,
            senderDisplayName,
            aiGenerated,
            extraData
        });

        // Return success optimistically - the message_sent event confirms delivery
        return { success: true };
    }

    /**
     * Legacy: Send a chat message (for backwards compatibility)
     */
    sendChatMessage() {
        if (!this.chatInput) return;

        const message = this.chatInput.value.trim();
        if (!message) return;

        const chatMessage = {
            type: 'chat',
            message: message,
            username: this.core.connectionId || 'Unknown',
            timestamp: Date.now()
        };

        // Display locally immediately
        this.displayChatMessage(chatMessage);

        // Send via feature_message protocol
        this.sendMessage('chat', chatMessage);

        this.chatInput.value = '';
    }

    // ==================== Message Editing ====================

    /**
     * Edit a previously sent message
     */
    async editMessage(messageId, newText) {
        try {
            const result = await this.sendRequest('edit_message', {
                messageId,
                newText
            });
            return result;
        } catch (error) {
            console.error('[Chat] editMessage error:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Delete a message
     */
    async deleteMessage(messageId) {
        try {
            const result = await this.sendRequest('delete_message', {
                messageId
            });
            return result;
        } catch (error) {
            console.error('[Chat] deleteMessage error:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== AI Suggestion Handling ====================

    /**
     * Get pending AI suggestion
     */
    getPendingSuggestion() {
        return this.pendingSuggestion;
    }

    /**
     * Approve AI suggestion
     */
    async approveSuggestion(editedText = null) {
        console.log('[Chat] Approving suggestion');

        const result = await this.sendMessage('approve_suggestion', {
            editedText
        });

        if (result?.success) {
            this.pendingSuggestion = null;
        }

        return result;
    }

    /**
     * Reject AI suggestion
     */
    async rejectSuggestion() {
        console.log('[Chat] Rejecting suggestion');

        const result = await this.sendMessage('reject_suggestion', {});

        this.pendingSuggestion = null;

        return result;
    }

    // ==================== Broadcast Controls ====================

    extendBroadcast(addMs) {
        this.sendMessage('extend_broadcast', { addMs });
    }

    dismissBroadcast() {
        this.sendMessage('dismiss_broadcast', {});
    }

    // ==================== Conversation to Dossier ====================

    /**
     * Add current conversation to dossier (AI-analyzed)
     */
    async addToDossier(sessionId = null) {
        try {
            return await this.sendRequest('add_to_dossier', { sessionId });
        } catch (error) {
            console.error('[Chat] addToDossier error:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== Voice Messages ====================

    /**
     * Send a voice message (base64 audio)
     */
    sendVoiceMessage(audioBase64, durationMs) {
        this.sendMessage('send_voice_message', { audioBase64, durationMs });
    }

    // ==================== Typing Interference ====================

    /**
     * Trigger a typing interference effect on the desktop
     */
    triggerTypingInterference(effect) {
        this.sendMessage('typing_interference', { effect });
    }

    // ==================== Ghost Typing ====================

    /**
     * Send a single ghost keystroke to the desktop
     */
    sendGhostKeystroke(char) {
        this.sendMessage('ghost_keystroke', { char });
    }

    /**
     * Clear ghost-typed text on the desktop
     */
    clearGhostText() {
        this.sendMessage('ghost_clear', {});
    }

    // ==================== Acknowledgment Stats ====================

    /**
     * Get broadcast acknowledgment stats
     */
    async getAcknowledgmentStats() {
        try {
            return await this.sendRequest('get_acknowledgment_stats', {});
        } catch (error) {
            console.error('[Chat] getAcknowledgmentStats error:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== History ====================

    /**
     * Send a request and wait for response
     */
    async sendRequest(cmd, payload = {}) {
        return new Promise((resolve, reject) => {
            const metadata = this.constructor.getMetadata();
            const messageId = this.core.sendFeatureMessage({
                feature: metadata.id,
                version: metadata.version,
                cmd,
                payload
            });

            // Store pending request
            this.pendingRequests.set(messageId, { resolve, reject });

            // Timeout after 10 seconds
            setTimeout(() => {
                if (this.pendingRequests.has(messageId)) {
                    this.pendingRequests.delete(messageId);
                    reject(new Error('Request timeout'));
                }
            }, 10000);
        });
    }

    /**
     * Handle response to a pending request
     */
    handleResponse(messageId, response) {
        const pending = this.pendingRequests.get(messageId);
        if (pending) {
            this.pendingRequests.delete(messageId);
            pending.resolve(response);
        }
    }

    /**
     * Get chat sessions
     */
    async getSessions(options = {}) {
        try {
            return await this.sendRequest('get_sessions', options);
        } catch (error) {
            console.error('[Chat] getSessions error:', error);
            return { success: false, error: error.message, sessions: [] };
        }
    }

    /**
     * Get messages for a session
     */
    async getSessionMessages(sessionId, options = {}) {
        try {
            return await this.sendRequest('get_messages', {
                sessionId,
                ...options
            });
        } catch (error) {
            console.error('[Chat] getSessionMessages error:', error);
            return { success: false, error: error.message, messages: [] };
        }
    }

    /**
     * Get recent messages
     */
    async getRecentMessages(options = {}) {
        try {
            return await this.sendRequest('get_messages', options);
        } catch (error) {
            console.error('[Chat] getRecentMessages error:', error);
            return { success: false, error: error.message, messages: [] };
        }
    }

    /**
     * Get messages for the current active session (used for reconnect sync)
     * This fetches the chat history from the desktop on page load/reconnect
     */
    async getCurrentSessionMessages(options = {}) {
        try {
            const result = await this.sendRequest('get_current_session_messages', options);
            if (result?.success && result.hasSession) {
                // Update local session state
                this.currentSession = result.session;
                this.currentModeConfig = result.modeConfig || normalizeModeConfig(result.displayMode || 'conversation');
                this.currentAiMode = result.aiMode || 'human';
            }
            return result;
        } catch (error) {
            console.error('[Chat] getCurrentSessionMessages error:', error);
            return { success: false, error: error.message, messages: [] };
        }
    }

    /**
     * Search messages
     */
    async searchMessages(searchText, options = {}) {
        try {
            return await this.sendRequest('search_messages', {
                searchText,
                ...options
            });
        } catch (error) {
            console.error('[Chat] searchMessages error:', error);
            return { success: false, error: error.message, messages: [] };
        }
    }

    /**
     * Export session
     */
    async exportSession(sessionId) {
        try {
            return await this.sendRequest('export_session', { sessionId });
        } catch (error) {
            console.error('[Chat] exportSession error:', error);
            return { success: false, error: error.message };
        }
    }

    // ==================== Event Subscriptions ====================

    /**
     * Subscribe to chat events (for React components)
     */
    subscribe(eventType, callback) {
        if (!this.eventSubscribers.has(eventType)) {
            this.eventSubscribers.set(eventType, new Set());
        }
        this.eventSubscribers.get(eventType).add(callback);

        // Return unsubscribe function
        return () => {
            const subscribers = this.eventSubscribers.get(eventType);
            if (subscribers) {
                subscribers.delete(callback);
            }
        };
    }

    /**
     * Emit event to subscribers
     */
    emitEvent(eventType, data) {
        const subscribers = this.eventSubscribers.get(eventType);
        if (subscribers) {
            subscribers.forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`[Chat] Error in event subscriber for ${eventType}:`, error);
                }
            });
        }
    }

    // ==================== Connection Management ====================

    /**
     * Enable chat (called when connected to peer)
     */
    enable() {
        this.chatEnabled = true;
        if (this.isActive) {
            this.enableUI();
        }
        console.log('[Chat] Chat enabled');

        // Sync personality presets from desktop
        this.fetchPersonalityPresets();

        // Fetch AI auto mode settings
        this.getAiAutoSettings();
    }

    /**
     * Disable chat (called when disconnected from peer)
     */
    disable() {
        this.chatEnabled = false;
        if (this.isActive) {
            this.disableUI();
        }
        console.log('[Chat] Chat disabled');
    }

    // ==================== Display ====================

    /**
     * Display a chat message in the chat box
     */
    displayChatMessage(messageData) {
        // Add to local history
        this.messageHistory.push(messageData);

        // If chat view isn't active, we can't display messages
        if (!this.chatMessages) {
            console.log('[Chat] Message received but chat view not active');
            return;
        }

        const messageEl = document.createElement('div');
        messageEl.className = 'chat-message';

        const usernameEl = document.createElement('div');
        usernameEl.className = 'username';
        usernameEl.textContent = messageData.username ||
            messageData.senderDisplayName ||
            (messageData.fromId ? messageData.fromId.substring(0, 8) : 'Unknown');

        const textEl = document.createElement('div');
        textEl.className = 'message-text';
        textEl.textContent = messageData.message || messageData.messageText;

        const timeEl = document.createElement('div');
        timeEl.className = 'timestamp';
        const time = messageData.timestamp ? new Date(messageData.timestamp) : new Date();
        timeEl.textContent = time.toLocaleTimeString();

        messageEl.appendChild(usernameEl);
        messageEl.appendChild(textEl);
        messageEl.appendChild(timeEl);

        this.chatMessages.appendChild(messageEl);

        // Auto-scroll to bottom
        this.chatMessages.scrollTop = this.chatMessages.scrollHeight;
    }

    // ==================== Cleanup ====================

    async cleanup() {
        console.log('[Chat] Cleaning up...');
        this.disable();
        await this.onDeactivate();

        // Clear subscriptions
        this.eventSubscribers.clear();

        // End session if active
        if (this.currentSession) {
            await this.endSession('cleanup');
        }
    }
}
